import { useState } from 'react';

import { Paper } from '@mui/material';

import { ConfirmationDialog } from '@/features/Dashboard/pages/Edit/components';
import { useDeletePastExport } from '@/features/DataHub/api';
import { usePastExportDetails } from '@/features/DataHub/hooks';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import { useExportRequest } from '@/features/DataHub/modules/Export/context/ExportRequestContext';
import { ExportItemActions } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemActions';
import { ExportItemDetails } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemDetails';
import { ExportItemHeader } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemHeader';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';

export const ExportItem = ({ item }: { item: ExportItemType }) => {
  const { exportDataWithParams, setExcelExportStatus } = useExportRequest();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading } = usePastExportDetails(item.id);
  const { mutate, isPending: isDeleting } = useDeletePastExport();

  const handleDelete = () => {
    setConfirmOpen(false);
    mutate(item.id);
  };

  const handleRegenerate = async () => {
    if (!data) return;
    setExcelExportStatus('loading');

    // First save to past exports
    await exportDataWithParams({
      id: item.id,
      name: data.name,
      type: data.type,
      exportSubType: data.exportSubType,
      from: data.from,
      to: data.to,
      includeMetadata: data.metadata === 'true',
      timezone: data.timezone,
      dateFormat: data.dateFormat,
      symbols: data.symbols
    });

    setExcelExportStatus('idle');
  };

  const option = data ? (optionById.get(data.type) ?? null) : null;
  const isOngoing = item.exportStatus === 'ongoing';

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <ExportItemHeader
        name={item.name}
        status={item.exportStatus}
      />
      <ExportItemDetails
        data={data}
        isLoading={isLoading}
        optionLabel={option?.label}
      />
      <ExportItemActions
        canDelete={!isOngoing || data === undefined}
        canRegenerate={
          (item.exportStatus === 'finished' || item.exportStatus === 'failed') && Boolean(data)
        }
        isDeleting={isDeleting}
        onDelete={() => {
          setConfirmOpen(true);
        }}
        onRegenerate={handleRegenerate}
      />
      <ConfirmationDialog
        cancelText='Cancel'
        confirmText='Delete'
        open={confirmOpen}
        text='This export will be permanently deleted.'
        title='Delete past export?'
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={handleDelete}
      />
    </Paper>
  );
};
